import type React from 'react'
import { Box, Container } from '@chakra-ui/react'
import type { ICategory } from '@/app/[locale]/_types'
import FixedBar from '../../Categories/FixedBar'
import StaticBar from '../../Categories/StaticBar'
import { useInfiniteQuery } from '@tanstack/react-query'
import { getCategoryList } from '@/app/[locale]/_service/category.service'
import { useSelector } from 'react-redux'
import { IRedux } from '@/app/[locale]/_store/types'
import { useMenuContext } from '../utils/client'
import { useDynamicLanguage } from '@/app/[locale]/_hooks/useShipper'

interface CategoriesSectionProps {
  active?: string
  catalogRef: React.RefObject<HTMLDivElement | null>
  count?: number
  isSeperatePage: boolean
  activeSlug?: string
  serverData?: ICategory[]
}

const CategoriesSection = ({
  active,
  catalogRef,
  count,
  serverData,
  isSeperatePage,
}: CategoriesSectionProps) => {
  const settings = useSelector((state: IRedux) => state.settings)
  const { platform_id, effectiveMenuId, deliveryType } = useMenuContext()
  const { useDynamicLanguage: dynamic, isShipperResolved } = useDynamicLanguage()

  const shipperId = settings?.data?.shipper_id || ''

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        'marketplace-categories',
        shipperId,
        effectiveMenuId,
        active,
        dynamic,
      ],
      queryFn: async ({ pageParam = 1 }) => {
        const response = await getCategoryList(
          {
            page: pageParam,
            limit: 50,
            is_active: true,
            has_product: true,
            menu_id: effectiveMenuId,
            delivery_type: deliveryType,
          },
          shipperId,
          platform_id,
          dynamic
        )
        return {
          categories: response?.data?.categories || [],
          count: response?.data?.count || 0,
          pageParam,
        }
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) => lastPage.pageParam + 1,
      enabled: isSeperatePage && Boolean(shipperId) && isShipperResolved,
    })

  const categories =
    (isSeperatePage
      ? data?.pages?.flatMap((page) => page.categories)
      : serverData) || []

  return (
    <Box pb={{ base: 4, lg: 0 }} zIndex={10}>
      <div ref={catalogRef}>
        <FixedBar
          data={categories}
          instanceRef={catalogRef}
          isSeparatePage={isSeperatePage}
        />
        <Container zIndex={1}>
          <StaticBar
            data={categories}
            count={count}
            showImage={true}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isSeperatePage={isSeperatePage}
            isFetchingNextPage={isFetchingNextPage}
          />
        </Container>
      </div>
    </Box>
  )
}

export default CategoriesSection
